import React, { useEffect, useState } from "react";
import CustomTable from "../../../components/Tables/CustomTable";
import { MoreVertical } from "lucide-react";
import FilterByButton from "../../ui/FilterButton";
import DropdownMenu from "../../ui/DropdownMenu";
import Button from "../../ui/Button";
import AddCourierCompany from "./AddCourierModal";
import EditCourierModal from "./EditCourierModal";
import DeleteCourierCompany from "./DeleteCourierCompany";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../redux/app/hooks";
import { fetchAllCouriers } from "../../../redux/features/CourrierSlice";
import moment from "moment";
import WhiteTitledCard from "./../../../components/cards/WhiteTitledCard";
import SearchInput from "../../../components/form/SearchInput";
import { cleanObject, isOppAdmin } from "../../../helpers";

const CourierTable: React.FC = () => {
  const [openAddCourier, setOpenCourier] = useState(false);
  const [openEditCourier, setOpenEditCourier] = useState(false);
  const [deleteCourier, setDeleteCourier] = useState(false);
  const { couriers, loading } = useAppSelector((state) => state.courier);
  const { user } = useAppSelector((state) => state.auth);
  const [courierId, setCourierId] = useState<string>();
  const [pageLimit, setPageLimit] = useState<number>(10);
  const [page, setPage] = useState<number>(0);
  const [search, setSearch] = useState("");
  const [inputValue, setInputValue] = useState("");
  const [status, setStatus] = useState("");

  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const columns = [
    { label: "S/N", field: "id" },
    { label: "Company Name", field: "company_name" },
    { label: "Email Address", field: "email" },
    { label: "Slug", field: "slug" },
    { label: "Phone Number", field: "phone_number" },
    { label: "Reg Date", field: "reg_date" },
    { label: "Status", field: "status" },
    { label: "Action", field: "action" },
  ];

  const filterOptions = [
    { label: "All", value: "" },
    { label: "Active", value: "ACTIVE" },
    { label: "Disabled", value: "DISABLED" },
  ];

  const dropdownOptions = [
    {
      label: "View courier company",
      onClick: (id: string) => navigate(`/courier-management/${id}`),
      returnBy: "id",
    },
    {
      label: "Edit courier company",
      onClick: (id: string) => {
        setOpenEditCourier(true);
        setCourierId(id);
      },
      returnBy: "id",
    },
    ...(!isOppAdmin(user)
      ? [
          {
            label: <span className="text-red-500">Delete courier company </span>,
            onClick: (id: string) => {
              setDeleteCourier(true);
              setCourierId(id);
            },
            returnBy: "id",
          },
        ]
      : []),
  ];

  const courierData = couriers?.data?.items?.map((item: any, index: number) => {
    return {
      _courierId: item?.id,
      id: index + 1,
      company_name: (
        <div className="flex items-center gap-2">
          {/* <img
          src="/assets/images/svg/avatar.svg"
          alt="User Avatar"
          className="w-8 h-8 rounded-full"
        />{" "} */}
          <span>{item?.name}</span>
        </div>
      ),
      slug: item?.slug,
      email: item?.email,
      phone_number: item?.phoneNumber,
      reg_date: moment(item?.createdAt).format("Do MMM YYYY"),
      status: (
        <div
          className={`px-3 text-center py-1 rounded-full text-sm font-semibold ${
            item?.status === "ACTIVE"
              ? "bg-green-100 text-green-700"
              : "bg-red-100 text-red-700"
          }`}
        >
          {item?.status}
        </div>
      ),
      ...(user?.user?.roles !== "INVESTOR" && {
        action: (
          <DropdownMenu
            options={dropdownOptions}
            icon={<MoreVertical style={{ cursor: "pointer" }} />}
            item={item}
          />
        ),
      }),
    };
  });

  const tabList = [
    {
      id: 1,
      name: "Courier Companies",
      count: couriers?.data?.meta?.totalItems ?? 0,
    },
  ];

  const handleFilterSelect = (value: string) => {
    setStatus(value);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      setSearch(inputValue);
    }
  };

  useEffect(() => {
    const params = cleanObject({
      page: page + 1,
      limit: pageLimit,
      status,
      search,
    });
    dispatch(fetchAllCouriers(params));
  }, [dispatch, page, pageLimit, status, search]);

  return (
    <WhiteTitledCard>
      <div className="px-6">
        <div className="flex flex-row justify-between sm:flex-row flex-wrap items-stretch sm:items-center gap-3 w-full">
          {tabList?.map((tab) => (
            <div
              key={tab.id}
              className={`space-x-2 cursor-pointer text-sm font-semibold p-2 text-black border-b-2 border-primary
                 `}
            >
              <span>{tab.name}</span>
              {tab?.count && (
                <span className={"bg-lightPurple px-2  text-accent rounded-md"}>
                  {tab?.count}
                </span>
              )}
            </div>
          ))}
          <div className="flex-col-reverse sm:flex sm:items-center sm:flex-row  gap-4 flex">
            <FilterByButton
              options={filterOptions}
              onSelect={handleFilterSelect}
              buttonLabel="Filter by status"
            />

            <SearchInput
              placeholder="Search"
              onChange={(e: any) => setInputValue(e.target.value)}
              value={inputValue}
              onKeyDown={handleKeyDown}
            />
            <Button
              layout="toolbar" size="sm"
              label="filter"
              onClick={() => setSearch(inputValue)}
              classNames="hidden sm:flex"
            />

            <Button
              layout="toolbar" size="sm" minWidthClass="min-w-[250px]"
              label="Add courier Companies"
              onClick={() => setOpenCourier(true)}
            />
          </div>
        </div>
        <CustomTable
          data={{ columns, rows: courierData }}
          onRowClick={(row) =>
            navigate(`/courier-management/${row._courierId}`)
          }
          showFooter={true}
          noRecordText="No courier company have been created yet."
          loading={loading}
          totalPages={couriers?.data?.meta?.totalPages}
          itemsPerPage={couriers?.data?.meta?.itemsPerPage}
          currentPage={page}
          onLimitChange={(value: number) => setPageLimit(value)}
          onPageChange={(value: number) => setPage(value)}
        />
        <AddCourierCompany
          open={openAddCourier}
          onClose={() => setOpenCourier(false)}
          page={page + 1}
          limit={pageLimit}
        />
        <EditCourierModal
          open={openEditCourier}
          onClose={() => setOpenEditCourier(false)}
          courierId={courierId}
          page={page + 1}
          limit={pageLimit}
        />
        <DeleteCourierCompany
          isOpen={deleteCourier}
          onClose={() => setDeleteCourier(false)}
          courierId={courierId}
          page={page + 1}
          limit={pageLimit}
        />
      </div>
    </WhiteTitledCard>
  );
};

export default CourierTable;
